import { useEffect, useState } from 'react';
import { AppState } from 'react-native';
import { addDays, parseIsoDate, todayIso } from '../utils/localDate';

/** Milliseconds from `now` to the start of the next local day. */
export function msUntilNextDay(now: Date = new Date()): number {
  const next = parseIsoDate(addDays(todayIso(now), 1));
  next.setHours(0, 0, 0, 0);
  return Math.max(0, next.getTime() - now.getTime());
}

/**
 * Today's calendar day, re-read at midnight and whenever the app comes back
 * to the foreground.
 *
 * A screen that derives what it shows from "today" has nothing else telling it
 * the day changed: a phone left on the grocery list overnight keeps its state,
 * and a browser throttles a background tab's timers, so the midnight timer
 * alone can fire hours late. The foreground check is what catches the morning.
 */
export function useToday(): string {
  const [today, setToday] = useState(todayIso);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const refresh = () => setToday(todayIso());
    const schedule = () => {
      // A second past midnight, so the timer never lands on the old day.
      timer = setTimeout(() => {
        refresh();
        schedule();
      }, msUntilNextDay() + 1000);
    };
    schedule();

    const subscription = AppState.addEventListener('change', (next) => {
      if (next === 'active') refresh();
    });

    return () => {
      if (timer) clearTimeout(timer);
      subscription.remove();
    };
  }, []);

  return today;
}
